'use client';

/**
 * Context pour la configuration globale de la recherche d'entités
 *
 * Principe SOLID: Single Responsibility
 * - Partage uniquement la configuration, pas l'état de recherche (géré par InstantSearch)
 *
 * Principe Dependency Inversion:
 * - Les composants dépendent du context (abstraction), pas de l'implémentation
 *
 * @module EntitySearchConfigContext
 */

import { createContext, useContext } from 'react';

import type { EntityType } from '@/lib/config/SearchEngine/entityFields';

// ============================================================================
// Types
// ============================================================================

/**
 * Configuration globale de la recherche d'entités
 */
export interface EntitySearchConfigContextValue {
  /**
   * Type d'entité (auteurs, editeurs, relieurs)
   */
  entityType: EntityType;

  /**
   * Nom de l'index Meilisearch
   * @example 'auteurs', 'editeurs', 'relieurs'
   */
  indexName: string;

  /**
   * Messages personnalisés pour le state NoResults
   */
  customNoResultsMessages?: {
    titre?: string;
    message?: string | React.ReactNode;
  };

  /**
   * Afficher un lien vers la recherche globale quand aucun résultat
   * @default false
   */
  linkToGlobalSearchOnNoResults?: boolean;
}

/**
 * Props du Provider
 */
export interface EntitySearchConfigProviderProps extends EntitySearchConfigContextValue {
  children: React.ReactNode;
}

// ============================================================================
// Context
// ============================================================================

/**
 * Context React pour partager la configuration de recherche d'entités
 */
const EntitySearchConfigContext =
  createContext<EntitySearchConfigContextValue | null>(null);

// ============================================================================
// Provider Component
// ============================================================================

/**
 * Provider du context de configuration de recherche d'entités
 *
 * @example
 * ```tsx
 * <EntitySearchConfigProvider
 *   entityType="auteurs"
 *   indexName="auteurs"
 *   customNoResultsMessages={{ titre: "Aucun auteur trouvé" }}
 * >
 *   <EntitySearchInterface />
 * </EntitySearchConfigProvider>
 * ```
 */
export function EntitySearchConfigProvider({
  children,
  ...config
}: EntitySearchConfigProviderProps) {
  return (
    <EntitySearchConfigContext.Provider value={config}>
      {children}
    </EntitySearchConfigContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook pour accéder à la configuration de recherche d'entités
 *
 * @throws {Error} Si utilisé hors du EntitySearchConfigProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { entityType, indexName } = useEntitySearchConfig();
 *   // ...
 * }
 * ```
 */
export function useEntitySearchConfig(): EntitySearchConfigContextValue {
  const context = useContext(EntitySearchConfigContext);

  if (!context) {
    throw new Error(
      'useEntitySearchConfig must be used within EntitySearchConfigProvider',
    );
  }

  return context;
}
